import { IconGift, IconSpeakerphone } from '@tabler/icons-react';
import { fileUrl } from '../services/api';
import { BannerTemplate, BannerContent, BannerDesign, pickLang, mergeDesign, APP_TOKENS, Lang } from '../lib/bannerDesign';

interface PreviewBanner {
  template: BannerTemplate;
  content?: BannerContent | null;
  design?: BannerDesign | null;
  linkScreen?: string | null;
  linkUrl?: string | null;
}

/** Bannerni admin panelda ilovadagidek render qiladi (jonli preview). */
export function BannerPreview({ banner, theme, lang, width = 320 }: { banner: PreviewBanner; theme: 'light' | 'dark'; lang: Lang; width?: number }) {
  const tok = APP_TOKENS[theme];
  const c = pickLang(banner.content, lang);
  const d = mergeDesign(banner.template, banner.design);
  const th = theme === 'dark' ? d.dark : d.light;
  const hasLink = !!(banner.linkScreen || banner.linkUrl);

  const onLight = th.text !== 'dark';
  const fg = onLight ? '#FFFFFF' : '#1A1730';
  const fgDim = onLight ? 'rgba(255,255,255,0.9)' : 'rgba(20,18,40,0.68)';
  const grad = `linear-gradient(135deg, ${th.grad1 || th.bg || tok.brand}, ${th.grad2 || th.bg || tok.brand})`;
  const alignItems = d.align === 'center' ? 'center' : 'flex-start';
  const img = th.imageKey ? fileUrl(th.imageKey) : undefined;
  const R = 18;
  const H = 150; // barcha shablonlar bir xil balandlikda (app bilan mos)

  const canvas: React.CSSProperties = { background: tok.bg, borderRadius: 22, padding: 16, width: width + 32, display: 'inline-block' };

  let card: React.ReactNode = null;

  if (banner.template === 'HERO') {
    card = (
      <div style={{ width, height: H, borderRadius: R, overflow: 'hidden', position: 'relative', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', padding: 16,
        background: img ? `center/cover no-repeat url(${img})` : grad }}>
        {d.scrim !== false && <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(8,5,24,0.78), rgba(8,5,24,0.35) 46%, transparent 70%)' }} />}
        <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems, textAlign: d.align === 'center' ? 'center' : 'left', width: '100%' }}>
          {c.title && <div style={{ color: '#fff', fontSize: 19, fontWeight: 800, marginBottom: 4 }}>{c.title}</div>}
          {c.body && <div style={{ color: 'rgba(255,255,255,0.9)', fontSize: 13, marginBottom: 12 }}>{c.body}</div>}
          {d.showCta && c.cta && <span style={{ background: th.accent || '#fff', color: '#3A2596', fontSize: 13, fontWeight: 700, padding: '8px 14px', borderRadius: 999 }}>{c.cta} ›</span>}
        </div>
      </div>
    );
  } else if (banner.template === 'SOLID') {
    card = (
      <div style={{ width, height: H, borderRadius: R, padding: 16, display: 'flex', alignItems: 'center', gap: 12, background: th.useGradient ? grad : (th.bg || tok.brand) }}>
        {d.emoji && <div style={{ width: 40, height: 40, borderRadius: 13, background: 'rgba(255,255,255,0.92)', display: 'grid', placeItems: 'center', fontSize: 20, flex: 'none' }}>{d.emoji}</div>}
        <div style={{ flex: 1 }}>
          {c.title && <div style={{ color: fg, fontSize: 17, fontWeight: 800, marginBottom: 3 }}>{c.title}</div>}
          {c.body && <div style={{ color: fgDim, fontSize: 13 }}>{c.body}</div>}
        </div>
        {hasLink && <span style={{ color: fgDim, fontSize: 18 }}>›</span>}
      </div>
    );
  } else if (banner.template === 'GRADIENT') {
    card = (
      <div style={{ width, height: H, borderRadius: R, padding: 18, position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems, textAlign: d.align === 'center' ? 'center' : 'left', background: grad }}>
        <span style={{ position: 'absolute', width: 120, height: 120, top: -46, right: -30, borderRadius: '50%', background: 'rgba(255,255,255,0.16)' }} />
        <span style={{ position: 'absolute', width: 70, height: 70, bottom: -30, right: 60, borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />
        {c.title && <div style={{ color: '#fff', fontSize: 19, fontWeight: 800, marginBottom: 4, position: 'relative' }}>{d.emoji ? d.emoji + ' ' : ''}{c.title}</div>}
        {c.body && <div style={{ color: 'rgba(255,255,255,0.92)', fontSize: 13, position: 'relative', maxWidth: '85%' }}>{c.body}</div>}
        {d.showCta && c.cta && <span style={{ background: 'rgba(255,255,255,0.22)', color: '#fff', fontSize: 13, fontWeight: 700, padding: '7px 13px', borderRadius: 999, marginTop: 10, position: 'relative' }}>{c.cta}</span>}
      </div>
    );
  } else if (banner.template === 'SPLIT') {
    const accent = th.accent || tok.brand;
    card = (
      <div style={{ width, height: H, borderRadius: R, overflow: 'hidden', display: 'flex', background: tok.surface, border: `1px solid ${tok.border}` }}>
        {/* Media: rasm → admin kiritgan belgi → ikonka. QATTIQ YOZILGAN EMOJI ZAXIRASI YO'Q —
            `templateDefaults` da `emoji` bo'sh (loyiha qoidasi), shuning uchun zaxira HAR BIR
            rasmsiz SPLIT bannerda chiqib qolardi. Ilovadagi bilan bir xil (`app/src/components/
            BannerCard.tsx`: `megaphone-outline`), aks holda preview yolg'on ko'rsatardi. */}
        <div style={{ width: 96, flex: 'none', display: 'grid', placeItems: 'center', fontSize: 38, background: img ? `center/cover no-repeat url(${img})` : accent + '22' }}>
          {img ? '' : (d.emoji || <IconSpeakerphone size={34} stroke={1.8} color={accent} />)}
        </div>
        <div style={{ flex: 1, padding: 15, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          {c.title && <div style={{ color: tok.text, fontSize: 16, fontWeight: 800, marginBottom: 3 }}>{c.title}</div>}
          {c.body && <div style={{ color: tok.textDim, fontSize: 13, marginBottom: 8 }}>{c.body}</div>}
          {c.cta && <span style={{ color: accent, fontSize: 13, fontWeight: 700 }}>{c.cta} →</span>}
        </div>
      </div>
    );
  } else if (banner.template === 'MINIMAL') {
    const accent = th.accent || tok.coral;
    card = (
      <div style={{ width, height: H, borderRadius: R, padding: 16, display: 'flex', gap: 13, alignItems: 'center', background: tok.surface, border: `1px solid ${tok.border}` }}>
        <span style={{ width: 4, alignSelf: 'stretch', borderRadius: 4, background: accent, flex: 'none' }} />
        <div style={{ flex: 1 }}>
          {c.eyebrow && <div style={{ color: accent, fontSize: 11, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 5 }}>{c.eyebrow}</div>}
          {c.title && <div style={{ color: tok.text, fontSize: 16, fontWeight: 700, marginBottom: 3 }}>{c.title}</div>}
          {c.body && <div style={{ color: tok.textDim, fontSize: 13 }}>{c.body}</div>}
        </div>
        {hasLink && <span style={{ color: tok.textDim, fontSize: 16 }}>›</span>}
      </div>
    );
  } else {
    const seal = th.accent || '#EBA210';
    card = (
      <div style={{ width, height: H, borderRadius: R, padding: '0 18px', display: 'flex', alignItems: 'center', gap: 14, position: 'relative', overflow: 'hidden', background: th.bg || '#2A2140', border: `1px solid ${seal}59` }}>
        <span style={{ position: 'absolute', left: -9, top: '50%', marginTop: -9, width: 18, height: 18, borderRadius: '50%', background: tok.bg }} />
        <span style={{ position: 'absolute', right: -9, top: '50%', marginTop: -9, width: 18, height: 18, borderRadius: '50%', background: tok.bg }} />
        <span style={{ position: 'absolute', left: 50, top: 12, bottom: 12, borderLeft: `1.5px dashed ${seal}66` }} />
        {/* Muhr: admin belgi kiritgan bo'lsa o'shani, aks holda ikonka (avvalgi "sovg'a" emoji
            zaxirasi har bir TICKET bannerda chiqib qolardi). Ikonka rangi — muhr ostidagi siyoh
            fon: oltin doirada kontrast yuqori. Ilovadagi bilan bir xil (`gift-outline`). */}
        <div style={{ width: 46, height: 46, borderRadius: '50%', display: 'grid', placeItems: 'center', fontSize: 22, flex: 'none', background: `radial-gradient(circle at 30% 30%, ${seal}, ${seal})` }}>
          {d.emoji || <IconGift size={24} stroke={1.8} color={th.bg || '#2A2140'} />}
        </div>
        <div style={{ flex: 1 }}>
          {c.title && <div style={{ color: '#FCE8B6', fontSize: 16, fontWeight: 800, marginBottom: 2 }}>{c.title}</div>}
          {c.body && <div style={{ color: 'rgba(252,232,182,0.72)', fontSize: 12.5 }}>{c.body}</div>}
        </div>
      </div>
    );
  }

  return <div style={canvas}>{card}</div>;
}
